import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Switch, Route } from 'react-router-dom';
import loadable from '@loadable/component';
import { useAppDispatch } from 'hooks/redux';
import Api from 'services/Api';
import { experiments, getExperiment } from 'lib/analytics';
import MaintenanceNotice from 'components/MaintenanceNotice';
import Announcements from 'components/Notifications/Announcements';
import Carousel from 'components/Carousel';
import NestedPage from './components/NestedPage';
import './account.css';

const isUKGCAuthority = __ENV__.MRQ_GAMING_AUTHORITY === 'UKGC';

const Wallet = loadable(
  () => import(/* webpackChunkName: "AccountWallet" */ './components/Wallet')
);
const BankTransactions = loadable(
  () =>
    import(
      /* webpackChunkName: "AccountBankTransactions" */
      './components/BankTransactions'
    )
);
const GameTransactions = loadable(
  () =>
    import(
      /* webpackChunkName: "AccountGameTransactions" */
      './components/GameTransactions'
    )
);
const Responsible = loadable(
  () => import(/* webpackChunkName: "AccountResponsible" */ './components/Responsible')
);
const RealityCheck = loadable(
  () => import(/* webpackChunkName: "AccountRealityCheck" */ './components/EditRealityCheck')
);
const Profile = loadable(
  () => import(/* webpackChunkName: "AccountProfile" */ './components/Profile')
);
const ReferralStatus = loadable(
  () => import(/* webpackChunkName: "AccountReferralStatus" */ './components/ReferralStatus')
);

const LegacyMarketingPreferences = loadable(
  () =>
    import(
      /* webpackChunkName: "AccountMarketingPreferences" */
      './components/MarketingPreferences/MarketingPreferences'
    )
);

const AccountMarketingPreferences = loadable(
  () =>
    import(
      /* webpackChunkName: "AccountMarketingPreferences" */
      './components/AccountMarketingPreferences'
    )
);
const ChangePassword = loadable(
  () => import(/* webpackChunkName: "AccountChangePassword" */ './components/ChangePassword')
);
const AccountContent = loadable(
  () =>
    import(/* webpackChunkName: "AccountContent" */ './components/AccountContent/AccountContent')
);
const Feedback = loadable(
  () => import(/* webpackChunkName: "AccountFeedback" */ './components/Feedback/Feedback')
);
const Footer = loadable(() => import('components/Footer'));

const Account = ({ location }: { location: Location }) => {
  const dispatch = useAppDispatch();

  const isAccountMarketingPreferencesRedesign = Boolean(
    getExperiment(experiments.accountMarketingPreferencesRedesign.flag)?.key
  );

  useEffect(() => {
    void Api.actions.slot.listAssets()(dispatch);
    if (__ENV__.MRQ_BINGO_ENABLED === 'true') {
      void Api.actions.bingo.listAssets()(dispatch);
    }
  }, [dispatch]);

  return (
    <div className="account page">
      <div className="account__container">
        <div className="account__notifications">
          <MaintenanceNotice pathname={location.pathname} />
          <Announcements />
        </div>
        <Switch>
          <Route path="*/payment-methods">
            <NestedPage backButtonHref="./wallet" backButtonText="Back to wallet">
              <Wallet />
            </NestedPage>
          </Route>
          <Route path="*/transaction-history">
            <NestedPage backButtonHref="./wallet" backButtonText="Back to wallet">
              <BankTransactions />
            </NestedPage>
          </Route>
          <Route path="*/game-history">
            <NestedPage backButtonHref="./wallet" backButtonText="Back to wallet">
              <GameTransactions />
            </NestedPage>
          </Route>
          <Route path="*/edit-limits">
            <NestedPage backButtonHref="./limits" backButtonText="Back to Spend Limits">
              <Responsible />
            </NestedPage>
          </Route>
          <Route path="*/reality-check">
            <NestedPage backButtonHref="./limits" backButtonText="Back to Spend Limits">
              <RealityCheck />
            </NestedPage>
          </Route>
          <Route path="*/edit-profile">
            <NestedPage backButtonHref="./profile" backButtonText="Back to profile">
              <Profile />
            </NestedPage>
          </Route>
          {isUKGCAuthority && (
            <Route path="*/refer-a-friend">
              <NestedPage backButtonHref="./profile" backButtonText="Back to profile">
                <ReferralStatus />
              </NestedPage>
            </Route>
          )}
          <Route path="*/marketing-preferences">
            <NestedPage backButtonHref="./profile" backButtonText="Back to profile">
              {isAccountMarketingPreferencesRedesign ? (
                <AccountMarketingPreferences />
              ) : (
                <LegacyMarketingPreferences />
              )}
            </NestedPage>
          </Route>
          <Route path="*/change-password">
            <NestedPage backButtonHref="./profile" backButtonText="Back to profile">
              <ChangePassword />
            </NestedPage>
          </Route>
          <Route path="*/feedback">
            <NestedPage backButtonHref="./help" backButtonText="Back to help">
              <Feedback />
            </NestedPage>
          </Route>
          <Route>
            <Carousel name="accountMiddle" getView={<AccountContent />} dynamicHeight />
          </Route>
        </Switch>
      </div>
      <Footer />
      <Helmet>
        <title>My Account | MrQ</title>
      </Helmet>
    </div>
  );
};

export default Account;
